import React, { useState, useEffect } from 'react';
import { Space, Button, Typography, message, Form, Modal, Card, Row, Col } from 'antd';
import { 
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useDevice } from '../../contexts/DeviceContext';
import { secureLogger } from '../../utils/secureLogger';
import { API_CONFIG } from '../../config/api';
import DeviceBasicInfo from './DeviceBasicInfo';
import DeviceSettingsComponent from './DeviceSettings';
import DeviceHistory from './DeviceHistory';
import DeviceProfileManager from './DeviceProfileManager';
import DeviceCurrentStatus from './DeviceCurrentStatus';

const { Title } = Typography;

interface Device {
  id: string;
  device_id: string;
  imei: string;
  name: string;
  device_type: string;
  tags?: string[];
  operation_mode: 'batch' | 'realtime';
  last_seen_at?: string;
  created_at: string;
  heartbeatTrigger?: number;
}

interface DeviceSettings {
  id: string;
  profile_name: string;
  photo_enabled: boolean;
  audio_enabled: boolean;
  gps_enabled: boolean;
  photo_interval: number;
  gps_interval: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

interface CameraConfigState {
  revision: number;
  overrides: Record<string, string>;
  updated_at?: string | null;
  delivered?: boolean | null;
}

interface DeviceDetailProps {
  isSocketConnected?: boolean;
}

const DeviceDetail: React.FC<DeviceDetailProps> = ({ isSocketConnected = false }) => {
  const { devices, loadDevices } = useDevice();
  const { deviceId } = useParams<{ deviceId: string }>();
  const navigate = useNavigate();
  const [isBasicEditing, setIsBasicEditing] = useState(false);
  const [deviceMessages] = useState<any[]>([]);
  const [isSettingsEditing, setIsSettingsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [settingsForm] = Form.useForm();
  const [deviceSettings, setDeviceSettings] = useState({
    photo_enabled: true,
    audio_enabled: true,
    gps_enabled: true,
    photo_interval: 60,
    gps_interval: 10
  });
  const [settingsProfiles, setSettingsProfiles] = useState<DeviceSettings[]>([]);
  const [activeProfile, setActiveProfile] = useState('default');
  const [showCreateProfile, setShowCreateProfile] = useState(false);
  const [createProfileForm] = Form.useForm();
  const [networkHistory, setNetworkHistory] = useState<any[]>([]);
  const [batteryHistory, setBatteryHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyDays, setHistoryDays] = useState(7);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [localDevice, setLocalDevice] = useState<Device | null>(null);
  const [networkSummary, setNetworkSummary] = useState<any>(null);
  const [gnssSummary, setGnssSummary] = useState<any>(null);
  const [cameraConfig, setCameraConfig] = useState<CameraConfigState>({
    revision: 0,
    overrides: {},
    updated_at: null,
    delivered: null,
  });
  const [cameraSetDraft, setCameraSetDraft] = useState<string>('{}');
  const [cameraClearDraft, setCameraClearDraft] = useState<string>('');
  const [cameraPatchLoading, setCameraPatchLoading] = useState(false);
  
  const device = localDevice || devices.find(d => d.id === deviceId);
  const deviceApiId = device?.imei || device?.device_id || device?.id || deviceId;
  const cameraConfigDeviceId = device?.id || deviceId;
  
  // 日付範囲計算のユーティリティ関数
  const getDateRange = (days: number = 1) => {
    const now = new Date();
    const toTime = now.toISOString();
    const fromTime = new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString();
    return { fromTime, toTime };
  };

  // バッテリーデータのみ更新
  const loadBatteryData = async () => {
    if (!device) return;
    try {
      const { fetchAuthSession } = await import('../../auth/gateway');
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      
      if (!token) return;
      
      const { fromTime, toTime } = getDateRange(1);
      
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/devices/${device.id}/battery/snapshots?from_time=${fromTime}&to_time=${toTime}&limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setBatteryHistory(data.snapshots || []);
      }
    } catch (error) {
      secureLogger.error('Failed to load battery data', { error: 'Battery data request failed' });
    }
  };
  
  // ネットワークデータのみ更新
  const loadNetworkData = async () => {
    if (!device) return;
    try {
      const { fetchAuthSession } = await import('../../auth/gateway');
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      
      if (!token) return;
      
      const { fromTime, toTime } = getDateRange(1);
      
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/devices/${device.id}/network/snapshots?from_time=${fromTime}&to_time=${toTime}&limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setNetworkHistory(data.snapshots || []);
      } else {
        try {
          const errorText = await response.text();
          secureLogger.error('Network data API error', { status: response.status, error: 'API request failed' });
        } catch {
          secureLogger.error('Network data API error', { status: response.status, error: response.statusText });
        }
        setHistoryError('ネットワークデータの取得に失敗しました');
      }
    } catch (error) {
      secureLogger.error('Failed to load network data', { error: 'Network data request failed' });
    }
  };
  
  // ネットワークサマリーを取得
  const loadNetworkSummary = async () => {
    if (!device) return;
    try {
      const { fetchAuthSession } = await import('../../auth/gateway');
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      
      if (!token) return;
      
      secureLogger.debug('Loading network summary for device');
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/devices/${device.id}/network/`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setNetworkSummary(data);
      } else {
        secureLogger.warn('Network summary failed', { status: response.status });
      }
    } catch (error) {
      secureLogger.error('Failed to load network summary', { error: 'Network request failed' });
    }
  };
  
  // GNSSサマリーを取得
  const loadGnssSummary = async () => {
    if (!device) return;
    try {
      const { fetchAuthSession } = await import('../../auth/gateway');
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      
      if (!token) return;
      
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/devices/${device.id}/gnss/`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setGnssSummary(data);
      }
    } catch (error) {
      secureLogger.error('Failed to load GNSS summary', { error: 'GNSS request failed' });
    }
  };
  
  // WebSocketメッセージ処理
  useEffect(() => {
    if (deviceMessages.length > 0 && device) {
      const latestMessage = deviceMessages[deviceMessages.length - 1];
      
      // デバイス接続/切断メッセージの処理
      if ((latestMessage.type === 'device_connected' || latestMessage.type === 'device_disconnected') && 
          latestMessage.device_id === device.id) {
        
        // localDeviceを更新
        setLocalDevice({
          ...device,
          operation_mode: latestMessage.data?.operation_mode || device.operation_mode,
          last_seen_at: latestMessage.timestamp
        });
        
        // 接続時は詳細情報を取得
        if (latestMessage.type === 'device_connected') {
          loadDeviceHistory();
        }
      }
      
      // 状態変更通知の処理
      if (latestMessage.device_id === device.id) {
        if (latestMessage.type === 'battery_status_changed') {
          // バッテリー情報を更新
          loadBatteryData();
        } else if (latestMessage.type === 'network_status_changed') {
          // ネットワーク情報を更新
          loadNetworkData();
          loadNetworkSummary();
        } else if (latestMessage.type === 'gnss_status_changed') {
          // GNSS情報を更新
          loadGnssSummary();
        }
      }
      
      // システム通知の処理
      if (latestMessage.type === 'system_notification') {
        secureLogger.info('System notification received');
      }
      
      // デバイスハートビートの処理
      if (latestMessage.type === 'device_heartbeat' && latestMessage.device_id === device.id) {
        // 最終接続時刻を更新し、アニメーショントリガーを設定
        const now = Date.now();
        setLocalDevice({
          ...device,
          last_seen_at: latestMessage.data?.heartbeat_timestamp || latestMessage.timestamp,
          heartbeatTrigger: now
        });
      }
    }
  }, [deviceMessages.length, device?.id]);

  useEffect(() => {
    const foundDevice = devices.find(d => d.id === deviceId);
    if (foundDevice) {
      secureLogger.debug('Found device in DeviceDetail');
      setLocalDevice(foundDevice);
      loadDeviceSettings();
      loadCameraConfig();
      loadDeviceHistory();
      loadNetworkSummary();
      loadGnssSummary();
    }
  }, [devices, deviceId, historyDays]);

  const loadDeviceSettings = async () => {
    if (!device || !deviceApiId) return;
    try {
      const { fetchAuthSession } = await import('../../auth/gateway');
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      
      if (!token) throw new Error('認証トークンが取得できませんでした');
      
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DEVICES}/${deviceApiId}/settings`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettingsProfiles(data.settings || []);
        
        const activeProfile = data.settings?.find((p: DeviceSettings) => p.is_active);
        if (activeProfile) {
          setDeviceSettings({
            photo_enabled: activeProfile.photo_enabled,
            audio_enabled: activeProfile.audio_enabled,
            gps_enabled: activeProfile.gps_enabled,
            photo_interval: activeProfile.photo_interval,
            gps_interval: activeProfile.gps_interval
          });
          setActiveProfile(activeProfile.profile_name);
          settingsForm.setFieldsValue({
            photo_enabled: activeProfile.photo_enabled,
            audio_enabled: activeProfile.audio_enabled,
            gps_enabled: activeProfile.gps_enabled,
            photo_interval: activeProfile.photo_interval,
            gps_interval: activeProfile.gps_interval
          });
        }
      }
    } catch (error) {
      secureLogger.error('Failed to load device settings', { error: 'Settings request failed' });
    }
  };

  const loadCameraConfig = async () => {
    if (!device || !cameraConfigDeviceId) return;
    try {
      const { fetchAuthSession } = await import('../../auth/gateway');
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();

      if (!token) throw new Error('認証トークンが取得できませんでした');

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/v1/devices/${cameraConfigDeviceId}/camera-config`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      setCameraConfig({
        revision: data.revision ?? 0,
        overrides: data.overrides ?? {},
        updated_at: data.updated_at ?? null,
        delivered: data.delivered ?? null,
      });
      setCameraSetDraft(JSON.stringify(data.overrides ?? {}, null, 2));
      setCameraClearDraft('');
    } catch (error) {
      secureLogger.error('Failed to load camera config override', { error: 'Camera config request failed' });
    }
  };

  const handleApplyCameraConfigPatch = async () => {
    if (!device || !cameraConfigDeviceId) return;

    let setPayload: Record<string, string> = {};
    try {
      const parsed = JSON.parse(cameraSetDraft || '{}');
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        message.error('set は JSON オブジェクトで指定してください');
        return;
      }
      setPayload = Object.entries(parsed).reduce<Record<string, string>>((acc, [key, value]) => {
        acc[String(key)] = String(value);
        return acc;
      }, {});
    } catch {
      message.error('set の JSON が不正です');
      return;
    }

    const clearPayload = cameraClearDraft
      .split(/[\n,]/)
      .map((v) => v.trim())
      .filter((v) => v.length > 0);

    if (Object.keys(setPayload).length === 0 && clearPayload.length === 0) {
      message.warning('set または clear のどちらかを指定してください');
      return;
    }

    try {
      setCameraPatchLoading(true);
      const { fetchAuthSession } = await import('../../auth/gateway');
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();

      if (!token) throw new Error('認証トークンが取得できませんでした');

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/v1/devices/${cameraConfigDeviceId}/camera-config`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            set: setPayload,
            clear: clearPayload,
          }),
        }
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      setCameraConfig({
        revision: data.revision ?? 0,
        overrides: data.overrides ?? {},
        updated_at: data.updated_at ?? null,
        delivered: data.delivered ?? null,
      });
      setCameraSetDraft(JSON.stringify(data.overrides ?? {}, null, 2));
      setCameraClearDraft('');
      message.success(
        data.delivered
          ? 'カメラ設定を更新し、デバイスへ即時配信しました'
          : 'カメラ設定を更新しました（デバイス未接続時は次回接続で反映）'
      );
    } catch (error) {
      secureLogger.error('Failed to patch camera config override', { error: 'Camera config patch failed' });
      message.error('カメラ設定の更新に失敗しました');
    } finally {
      setCameraPatchLoading(false);
    }
  };

  const loadDeviceHistoryWithRange = async (fromTime: string, toTime: string) => {
    if (!device) return;
    setHistoryLoading(true);
    setHistoryError(null);
    
    try {
      const { fetchAuthSession } = await import('../../auth/gateway');
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      
      if (!token) throw new Error('認証トークンが取得できませんでした');
      
      const limit = Math.min(200, Math.max(50, Math.ceil((new Date(toTime).getTime() - new Date(fromTime).getTime()) / (24 * 60 * 60 * 1000)) * 10));
      
      const networkResponse = await fetch(`${API_CONFIG.BASE_URL}/api/v1/devices/${device.id}/network/snapshots?from_time=${fromTime}&to_time=${toTime}&limit=${limit}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      let hasError = false;
      
      if (networkResponse.ok) {
        const networkData = await networkResponse.json();
        setNetworkHistory(networkData.snapshots || []);
      } else {
        try {
          const errorText = await networkResponse.text();
          secureLogger.error('Network API error (range)', { status: networkResponse.status, error: 'API request failed' });
        } catch {
          secureLogger.error('Network API error (range)', { status: networkResponse.status, error: networkResponse.statusText });
        }
        hasError = true;
      }
      
      // Load all battery data with pagination
      let allBatteryLogs = [];
      let offset = 0;
      let hasMore = true;
      
      while (hasMore) {
        const batteryResponse = await fetch(`${API_CONFIG.BASE_URL}/api/v1/devices/${device.id}/battery/snapshots?from_time=${fromTime}&to_time=${toTime}&limit=${limit}&offset=${offset}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        
        if (batteryResponse.ok) {
          const batteryData = await batteryResponse.json();
          allBatteryLogs.push(...(batteryData.snapshots || []));
          hasMore = batteryData.has_more || false;
          offset += limit;
        } else {
          hasError = true;
          break;
        }
      }
      
      setBatteryHistory(allBatteryLogs);
      
      if (hasError) {
        setHistoryError('一部のデータ取得に失敗しました');
      }
    } catch (error) {
      secureLogger.error('Failed to load device history with range', { error: 'History range request failed' });
      setHistoryError('デバイス履歴の取得に失敗しました');
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadDeviceHistory = async () => {
    if (!device) return;
    setHistoryLoading(true);
    setHistoryError(null);
    
    try {
      const { fetchAuthSession } = await import('../../auth/gateway');
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      
      if (!token) throw new Error('認証トークンが取得できませんでした');
      
      const { fromTime, toTime } = getDateRange(historyDays);
      const limit = Math.min(200, Math.max(50, historyDays * 10));
      
      const networkResponse = await fetch(`${API_CONFIG.BASE_URL}/api/v1/devices/${device.id}/network/snapshots?from_time=${fromTime}&to_time=${toTime}&limit=${limit}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      let hasError = false;
      
      if (networkResponse.ok) {
        const networkData = await networkResponse.json();
        setNetworkHistory(networkData.snapshots || []);
      } else {
        try {
          const errorText = await networkResponse.text();
          secureLogger.error('Network API error (history)', { status: networkResponse.status, error: 'API request failed' });
        } catch {
          secureLogger.error('Network API error (history)', { status: networkResponse.status, error: networkResponse.statusText });
        }
        hasError = true;
      }
      
      // Load all battery data with pagination
      let allBatteryLogs = [];
      let offset = 0;
      let hasMore = true;
      
      while (hasMore) {
        const batteryResponse = await fetch(`${API_CONFIG.BASE_URL}/api/v1/devices/${device.id}/battery/snapshots?from_time=${fromTime}&to_time=${toTime}&limit=${limit}&offset=${offset}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        
        if (batteryResponse.ok) {
          const batteryData = await batteryResponse.json();
          allBatteryLogs.push(...(batteryData.snapshots || []));
          hasMore = batteryData.has_more || false;
          offset += limit;
        } else {
          hasError = true;
          break;
        }
      }
      
      setBatteryHistory(allBatteryLogs);
      
      if (hasError) {
        setHistoryError('一部のデータ取得に失敗しました');
      }
    } catch (error) {
      secureLogger.error('Failed to load device history', { error: 'History request failed' });
      setHistoryError('デバイス履歴の取得に失敗しました');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSave = async () => {
    if (!device) return;
    try {
      const basicValues = await form.validateFields();
      setLoading(true);
      
      const { fetchAuthSession } = await import('../../auth/gateway');
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      
      // 基本情報更新のみ
      const basicResponse = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DEVICES}/${device.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: basicValues.name,
          tags: basicValues.tags || []
        }),
      });
      
      if (basicResponse.ok) {
        message.success('デバイス情報を更新しました');
        setIsBasicEditing(false);
        
        // ローカルデバイス状態を更新
        setLocalDevice({
          ...device,
          name: basicValues.name,
          tags: basicValues.tags || []
        });
        
        // グローバルデバイス一覧を更新
        loadDevices();
      } else {
        message.error('更新に失敗しました');
      }
    } catch (error) {
      message.error('入力値の検証に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    form.setFieldsValue({
      name: device?.name,
      tags: device?.tags || []
    });
    settingsForm.setFieldsValue(deviceSettings);
    setIsBasicEditing(true);
  };

  const handleCancel = () => {
    setIsBasicEditing(false);
    setIsSettingsEditing(false);
    form.resetFields();
    settingsForm.resetFields();
  };

  const handleSwitchProfile = async (profileName: string) => {
    if (!device || !deviceApiId) return;
    try {
      const { fetchAuthSession } = await import('../../auth/gateway');
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DEVICES}/${deviceApiId}/settings/activate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ profile_name: profileName }),
      });
      
      if (response.ok) {
        message.success(`プロファイル「${profileName}」に切り替えました`);
        await loadDeviceSettings();
      } else {
        message.error('プロファイルの切り替えに失敗しました');
      }
    } catch (error) {
      message.error('通信エラーが発生しました');
    }
  };

  const handleDeleteProfile = (profileName: string) => {
    if (!device || !deviceApiId || profileName === 'default') return;
    
    Modal.confirm({
      title: 'プロファイルを削除しますか？',
      content: `プロファイル「${profileName}」を削除します。この操作は元に戻せません。`,
      okText: '削除',
      okType: 'danger',
      cancelText: 'キャンセル',
      onOk: async () => {
        try {
          const { fetchAuthSession } = await import('../../auth/gateway');
          const session = await fetchAuthSession();
          const token = session.tokens?.accessToken?.toString();
          
          const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DEVICES}/${deviceApiId}/settings/profiles/${profileName}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` },
          });
          
          if (response.ok) {
            message.success('プロファイルを削除しました');
            await loadDeviceSettings();
          } else {
            message.error('プロファイルの削除に失敗しました');
          }
        } catch (error) {
          message.error('通信エラーが発生しました');
        }
      },
    });
  };

  const handleCreateProfile = async () => {
    if (!device || !deviceApiId) return;
    try {
      const values = await createProfileForm.validateFields();
      const { fetchAuthSession } = await import('../../auth/gateway');
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DEVICES}/${deviceApiId}/settings/profiles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          profile_name: values.profile_name,
          photo_enabled: values.photo_enabled ?? true,
          audio_enabled: values.audio_enabled ?? true,
          gps_enabled: values.gps_enabled ?? true,
          photo_interval: values.photo_interval ?? 60,
          gps_interval: values.gps_interval ?? 10,
        }),
      });
      
      if (response.ok) {
        message.success('プロファイルを作成しました');
        setShowCreateProfile(false);
        createProfileForm.resetFields();
        await loadDeviceSettings();
      } else {
        message.error('プロファイルの作成に失敗しました');
      }
    } catch (error) {
      message.error('入力値を確認してください');
    }
  };

  const handleDelete = () => {
    if (!device || !deviceApiId) return;
    
    Modal.confirm({
      title: 'デバイスを削除しますか？',
      content: `デバイス「${device.name}」を削除します。この操作は元に戻せません。`,
      okText: '削除',
      okType: 'danger',
      cancelText: 'キャンセル',
      onOk: async () => {
        try {
          const { fetchAuthSession } = await import('../../auth/gateway');
          const session = await fetchAuthSession();
          const token = session.tokens?.accessToken?.toString();
          
          const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DEVICES}/${deviceApiId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` },
          });
          
          if (response.ok) {
            message.success('デバイスを削除しました');
            navigate('/devices');
          } else {
            message.error('削除に失敗しました');
          }
        } catch (error) {
          message.error('通信エラーが発生しました');
        }
      },
    });
  };

  if (devices.length === 0) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          デバイス情報を読み込み中...
        </div>
      </Card>
    );
  }
  
  if (!device) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          デバイスが見つかりません
          <br />
          <Button 
            type="primary" 
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/devices')}
            style={{ marginTop: 16 }}
          >
            デバイス一覧に戻る
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div>
      {/* ヘッダー */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: 8,
        paddingBottom: 16,
        borderBottom: '1px solid #e0e0e0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Button 
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/devices')}
            type="text"
          >
            デバイス
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            {device.name}
          </Title>
        </div>
        
        <div />
      </div>
      
      <Row gutter={16}>
        {/* 左列: 静的情報・設定 */}
        <Col span={8}>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <DeviceBasicInfo 
              device={device}
              isEditing={isBasicEditing}
              form={form}
              onEditModeChange={setIsBasicEditing}
              onSave={handleSave}
            />

            <DeviceSettingsComponent
              deviceSettings={deviceSettings}
              isEditing={isSettingsEditing}
              settingsForm={settingsForm}
              settingsProfiles={settingsProfiles}
              activeProfile={activeProfile}
              isOnline={isSocketConnected}
              onSwitchProfile={handleSwitchProfile}
              onDeleteProfile={handleDeleteProfile}
              onCreateProfile={() => setShowCreateProfile(true)}
              onEditModeChange={setIsSettingsEditing}
              onSave={handleSave}
              cameraConfig={cameraConfig}
              cameraSetDraft={cameraSetDraft}
              cameraClearDraft={cameraClearDraft}
              onCameraSetDraftChange={setCameraSetDraft}
              onCameraClearDraftChange={setCameraClearDraft}
              onApplyCameraConfigPatch={handleApplyCameraConfigPatch}
              cameraPatchLoading={cameraPatchLoading}
            />

            <Card title="危険な操作" size="small">
              <Button type="link" danger onClick={handleDelete}>
                デバイスを削除
              </Button>
            </Card>
          </Space>
        </Col>

        {/* 右列: 動的情報・状態 */}
        <Col span={16}>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <DeviceCurrentStatus 
              device={localDevice || device}
              batteryHistory={batteryHistory}
              networkHistory={networkHistory}
              networkSummary={networkSummary}
              gnssSummary={gnssSummary}
            />

            <DeviceHistory
              networkHistory={networkHistory}
              batteryHistory={batteryHistory}
              historyLoading={historyLoading}
              historyDays={historyDays}
              historyError={historyError}
              onHistoryDaysChange={setHistoryDays}
              onDateRangeChange={(fromTime, toTime) => {
                // カスタム日付範囲での履歴読み込み
                loadDeviceHistoryWithRange(fromTime, toTime);
              }}
              onLoadHistory={loadDeviceHistory}
            />
          </Space>
        </Col>
      </Row>
      
      <DeviceProfileManager
        showCreateProfile={showCreateProfile}
        createProfileForm={createProfileForm}
        settingsProfiles={settingsProfiles}
        onCreateProfile={handleCreateProfile}
        onCancel={() => {
          setShowCreateProfile(false);
          createProfileForm.resetFields();
        }}
      />
    </div>
  );
};

export default DeviceDetail;
